import type {
  AppConfig,
  OwnerAccessProof,
  PublicSafeReceipt,
  Proposal,
  ProposalSignature,
  SafeDraftReceipt,
  SafeRecord,
} from "./types";

export class ApiError extends Error {
  readonly status: number;

  constructor(message: string, status: number) {
    super(message);
    this.name = "ApiError";
    this.status = status;
  }
}

function errorMessage(body: unknown, fallback: string): string {
  if (body && typeof body === "object") {
    const record = body as Record<string, unknown>;
    if (typeof record.message === "string") return record.message;
    if (record.error && typeof record.error === "object") {
      const nested = record.error as Record<string, unknown>;
      if (typeof nested.message === "string") return nested.message;
    }
  }
  return fallback;
}

async function request<T>(
  config: AppConfig,
  path: string,
  init: RequestInit = {},
): Promise<T> {
  const response = await fetch(`${config.backendUrl}${path}`, {
    ...init,
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
      ...init.headers,
    },
  });

  let body: unknown;
  try {
    body = await response.json();
  } catch {
    body = undefined;
  }

  if (!response.ok) {
    throw new ApiError(errorMessage(body, `Request failed (${response.status})`), response.status);
  }
  return body as T;
}

export interface SafeDraftInput {
  predictedAddress: string | null;
  draftId?: string;
  chainId: number;
  owners: string[];
  threshold: number;
  label?: string;
  salt: string;
  creator: string;
}

export interface SafeRegistrationMetadata {
  creationTxHash?: string;
  salt?: string;
  creator?: string;
  draftId?: string;
}

export async function createSafeDraft(
  config: AppConfig,
  input: SafeDraftInput,
  proof: OwnerAccessProof,
): Promise<SafeDraftReceipt> {
  const body = await request<{ safe: SafeDraftReceipt }>(config, "/safes/drafts", {
    method: "POST",
    headers: ownerAccessHeaders(proof),
    body: JSON.stringify(input),
  });
  return body.safe;
}

function ownerAccessHeaders(proof: OwnerAccessProof): HeadersInit {
  return {
    "X-L1X-Safe-Owner": proof.owner,
    "X-L1X-Safe-Access-Expires": proof.expiresAt,
    "X-L1X-Safe-Access-Signature": proof.signature,
  };
}

export async function fetchSafes(
  config: AppConfig,
  proof: OwnerAccessProof,
): Promise<SafeRecord[]> {
  const body = await request<{ safes?: SafeRecord[] } | SafeRecord[]>(config, "/safes", {
    headers: ownerAccessHeaders(proof),
  });
  return Array.isArray(body) ? body : (body.safes ?? []);
}

export async function registerSafe(
  config: AppConfig,
  address: string,
  label?: string,
  metadata: SafeRegistrationMetadata = {},
  proof?: OwnerAccessProof,
) {
  try {
    return await request<{ safe: PublicSafeReceipt }>(config, "/safes", {
      method: "POST",
      ...(proof ? { headers: ownerAccessHeaders(proof) } : {}),
      body: JSON.stringify({
        address,
        ...(label?.trim() ? { label: label.trim() } : {}),
        ...metadata,
      }),
    });
  } catch (error) {
    // Registration is idempotent from the UI's perspective. A conflict means the Safe is
    // already available and can be opened normally.
    if (error instanceof ApiError && error.status === 409) return null;
    throw error;
  }
}

export async function fetchSafe(
  config: AppConfig,
  address: string,
  proof?: OwnerAccessProof,
) {
  return request<unknown>(config, `/safes/${address}`, {
    ...(proof ? { headers: ownerAccessHeaders(proof) } : {}),
  });
}

export async function fetchPendingProposals(
  config: AppConfig,
  safeAddress: string,
): Promise<Proposal[]> {
  let body: { proposals?: Proposal[] } | Proposal[];
  try {
    body = await request<{ proposals?: Proposal[] } | Proposal[]>(
      config,
      `/safes/${safeAddress}/proposals?status=pending`,
    );
  } catch (error) {
    // A Safe can exist on chain while its creator-owned recovery draft is intentionally left
    // unpromoted. Treat that coordination-only state as an empty queue; the first valid owner
    // proposal signature safely promotes it on the server.
    if (error instanceof ApiError && error.status === 409) return [];
    throw error;
  }
  const proposals = Array.isArray(body) ? body : body.proposals;
  return (proposals ?? []).map((proposal) => ({
    ...proposal,
    signatures: Array.isArray(proposal.signatures) ? proposal.signatures : [],
  }));
}

export type ProposalInput = Omit<
  Proposal,
  | "id"
  | "safeAddress"
  | "status"
  | "executedTxHash"
  | "createdAt"
  | "signatures"
  | "createdBy"
> & { signature: string };

export async function createProposal(
  config: AppConfig,
  safeAddress: string,
  input: ProposalInput,
): Promise<Proposal> {
  const body = await request<{ proposal: Proposal }>(
    config,
    `/safes/${safeAddress}/proposals`,
    { method: "POST", body: JSON.stringify(input) },
  );
  return { ...body.proposal, signatures: body.proposal.signatures ?? [] };
}

export async function submitSignature(
  config: AppConfig,
  proposalId: string | number,
  signature: string,
  signerAddress: string,
): Promise<ProposalSignature> {
  const body = await request<{ signature: ProposalSignature }>(
    config,
    `/proposals/${proposalId}/signatures`,
    {
      method: "POST",
      body: JSON.stringify({ signature, signerAddress }),
    },
  );
  return body.signature;
}

export async function reportExecution(
  config: AppConfig,
  proposalId: string | number,
  txHash: string,
) {
  return request<unknown>(config, `/proposals/${proposalId}/executed`, {
    method: "POST",
    body: JSON.stringify({ txHash }),
  });
}
